import { useQuery } from "@tanstack/react-query";
import api from "../../http";
import { Manufacturer } from "@practium/features/products";

interface PaginatedResponse {
    docs: Manufacturer[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
}

interface UseGetManufacturersParams {
    page?: number;
    limit?: number;
    filter?: string;
    visibility?: string | string[];
    sortAsc?: boolean;
    enabled?: boolean;
}

export const useGetManufacturers = ({
    page = 1,
    limit = 100,
    filter,
    visibility,
    sortAsc,
    enabled = true
}: UseGetManufacturersParams = {}) => {
    const query = useQuery<PaginatedResponse>({
        queryKey: ['manufacturers', page, limit, filter, visibility, sortAsc],
        enabled,
        placeholderData: (previousData) => previousData,
        queryFn: async (): Promise<PaginatedResponse> => {
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('limit', String(limit));

            if (filter) params.set('filter', filter);
            if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));

            const visibilities = Array.isArray(visibility) ? visibility : visibility ? [visibility] : [];
            visibilities.forEach((item) => params.append('visibility', item));

            const { data } = await api.get<PaginatedResponse>('/manufacturer/findAll', {
                params
            });
            return data;
        },
    });

    return {
        ...query,
        data: query.data?.docs ?? [],
        pagination: query.data ? {
            totalDocs: query.data.totalDocs,
            totalPages: query.data.totalPages,
            page: query.data.page,
            hasPrevPage: query.data.hasPrevPage,
            hasNextPage: query.data.hasNextPage,
            prevPage: query.data.prevPage,
            nextPage: query.data.nextPage,
        } : null,
    };
};
